/*
 * Centre-active carousel with scroll-triggered playback.
 *
 * Two independent concerns, kept separate on purpose:
 *
 *   position — which slide is centred. Driven by arrows, dots, keyboard and touch, and applied
 *              as one custom property the CSS transform reads.
 *   playback — which video is running. Only ever the active slide, only while the section is on
 *              screen. Everything else is paused and rewound so nothing decodes off-screen.
 *
 * Autoplay only works muted and inline; the markup sets both. `play()` is still a promise that
 * can reject (a browser that declines, a source that never loads), so it is always caught —
 * an unhandled rejection here would surface as a console error on every scroll.
 */

const SWIPE_THRESHOLD = 40; // px before a horizontal drag counts as a swipe

class VpFormatCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-fc2-track]');
    this.slides = [...this.querySelectorAll('[data-fc2-slide]')];
    this.dots = [...this.querySelectorAll('[data-fc2-dot]')];
    if (!this.track || !this.slides.length) return;

    this.autoplay = this.dataset.autoplay === 'true';
    this.copyBox = this.dataset.copy ? document.getElementById(this.dataset.copy) : null;
    this.copyFade = Number(this.dataset.copyFade ?? 200);
    this.index = Math.min(Math.max(parseInt(this.dataset.start, 10) - 1 || 0, 0), this.slides.length - 1);
    this.onScreen = false;

    this.querySelector('[data-fc2-prev]')?.addEventListener('click', () => this.go(this.index - 1));
    this.querySelector('[data-fc2-next]')?.addEventListener('click', () => this.go(this.index + 1));
    this.dots.forEach((dot, i) => dot.addEventListener('click', () => this.go(i)));

    this.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') { this.go(this.index - 1); event.preventDefault(); }
      if (event.key === 'ArrowRight') { this.go(this.index + 1); event.preventDefault(); }
    });

    this.#bindSwipe();

    // Playback follows visibility, not just position: a carousel parked off-screen should not
    // be decoding video.
    this.observer = new IntersectionObserver(
      ([entry]) => { this.onScreen = entry.isIntersecting; this.#syncPlayback(); },
      { threshold: 0.35 }
    );
    this.observer.observe(this);

    this.#reserveCopyHeight();
    this.render();
  }

  disconnectedCallback() {
    clearTimeout(this.copyTimer);
    this.observer?.disconnect();
    this.slides.forEach((slide) => slide.querySelector('video')?.pause());
  }

  go(next) {
    this.index = Math.min(Math.max(next, 0), this.slides.length - 1);
    this.render();
  }

  render() {
    this.style.setProperty('--vp-fc2-index', String(this.index));

    this.slides.forEach((slide, i) => {
      const active = i === this.index;
      slide.classList.toggle('is-active', active);
      // Neighbours are decorative at 35% opacity; keep them out of the tab order and off the
      // screen-reader's list rather than announcing three slides at once.
      slide.setAttribute('aria-hidden', active ? 'false' : 'true');
    });

    this.dots.forEach((dot, i) => dot.setAttribute('aria-current', i === this.index ? 'true' : 'false'));

    const prev = this.querySelector('[data-fc2-prev]');
    const next = this.querySelector('[data-fc2-next]');
    if (prev) prev.disabled = this.index === 0;
    if (next) next.disabled = this.index === this.slides.length - 1;

    this.#swapCopy();
    this.#syncPlayback();
  }

  /*
   * Cross-fade the heading and body to the active slide's own copy.
   *
   * The text is replaced at zero opacity rather than layered, so the outgoing and incoming
   * versions are never legible at the same time — which is the whole point of the fade.
   */
  #swapCopy() {
    if (!this.copyBox) return;

    const source = this.slides[this.index]?.querySelector('[data-fc2-copy]');
    if (!source) return;

    // Compared by markup, not by element: slides that fall back to the section's copy carry
    // identical text, and fading between two identical paragraphs is just a flicker.
    const markup = source.innerHTML;
    if (markup === this.shownCopy) return;
    this.shownCopy = markup;

    const paint = () => this.copyBox.replaceChildren(source.content.cloneNode(true));

    // First paint, or a shop that asked for less motion: swap outright.
    if (!this.copyPainted || this.copyFade === 0 ||
        window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.copyPainted = true;
      paint();
      return;
    }

    clearTimeout(this.copyTimer);
    this.copyBox.classList.add('is-fading');
    this.copyTimer = setTimeout(() => {
      paint();
      this.copyBox.classList.remove('is-fading');
    }, this.copyFade);
  }

  /*
   * Hold the copy box at the height of its tallest version, so a longer heading on one slide
   * does not shove the carousel down the page mid-fade.
   */
  #reserveCopyHeight() {
    if (!this.copyBox) return;

    const sources = this.slides
      .map((slide) => slide.querySelector('[data-fc2-copy]'))
      .filter(Boolean);
    if (!sources.length) return;

    const restore = [...this.copyBox.childNodes];
    let tallest = this.copyBox.offsetHeight;
    for (const source of sources) {
      this.copyBox.replaceChildren(source.content.cloneNode(true));
      tallest = Math.max(tallest, this.copyBox.offsetHeight);
    }
    this.copyBox.replaceChildren(...restore);
    this.copyBox.style.minBlockSize = `${tallest}px`;
  }

  #syncPlayback() {
    this.slides.forEach((slide, i) => {
      const video = slide.querySelector('video');
      if (!video) return;

      if (this.autoplay && this.onScreen && i === this.index) {
        // `preload="none"` keeps the bytes off the wire until a slide is actually wanted.
        if (video.preload === 'none') video.preload = 'auto';
        video.muted = true;          // reasserted: a muted attribute alone is not always enough
        video.playsInline = true;
        video.play().catch(() => {}); // a declined autoplay is not an error worth surfacing
      } else if (!video.paused) {
        video.pause();
        if (i !== this.index) video.currentTime = 0;
      }
    });
  }

  #bindSwipe() {
    let startX = null;
    let startY = null;

    this.addEventListener('pointerdown', (event) => {
      if (event.pointerType === 'mouse') return; // dragging with a mouse fights text selection
      startX = event.clientX;
      startY = event.clientY;
    }, { passive: true });

    this.addEventListener('pointerup', (event) => {
      if (startX === null) return;
      const dx = event.clientX - startX;
      const dy = event.clientY - startY;
      startX = startY = null;
      // Ignore anything more vertical than horizontal so a page scroll never flips a slide.
      if (Math.abs(dx) < SWIPE_THRESHOLD || Math.abs(dy) > Math.abs(dx)) return;
      this.go(this.index + (dx < 0 ? 1 : -1));
    }, { passive: true });
  }
}

if (!customElements.get('vp-format-carousel')) {
  customElements.define('vp-format-carousel', VpFormatCarousel);
}
